import { Box, CircularProgress, GridLegacy as Grid, Stack, styled, useTheme } from '@mui/material'
import { User, UserSettingsFormType, UserSettingsProps } from './types'
import {
    CircularImage,
    ConfirmationDialog,
    FormInputLabel,
    FormSelectField,
    FormTemplate,
    FormTextField,
    LoadingSkeleton,
    Typography,
    validators,
} from 'ComponentLibrary'
import { PADDING_X, USER_SETTINGS_COPY } from './constants'
import { ReactElement, cloneElement, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
    CertificateIcon,
    EnvelopeIcon,
    IdentificationCardIcon,
    PhoneIcon,
    TranslateIcon,
    UploadSimpleIcon,
    UsersThreeIcon,
    XIcon,
} from '@phosphor-icons/react'
import { TextButtonNew } from 'ComponentLibrary/src/piazza/buttons'
import { Typography as PiazzaTypography } from 'ComponentLibrary/src/piazza'
import { FormDropdown, FormTextInput, TextInput } from 'ComponentLibrary/src/piazza/forms'
import { tokens } from 'ComponentLibrary/style-dictionary/output'

function isAppraiserUser(user: User | undefined) {
    return user?.role === 'appraiser'
}

export const UserSettings = ({
    user,
    teams = [],
    updateUserSettings,
    isUserPending,
    callToAction,
    isUpdatingUserSettings,
    copy = USER_SETTINGS_COPY,
    localeOptions,
    showLocaleSetting = true,
    currentLocale,
    layout = 'page',
    uploadImage,
    isUploadingImage,
    isRemovingImage,
    onModalFormStateChange,
    removeImage,
}: UserSettingsProps) => {
    const theme = useTheme()
    const profileImageInputRef = useRef<HTMLInputElement>(null)
    const [isRemoveImageConfirmationOpen, setIsRemoveImageConfirmationOpen] = useState(false)

    const isAppraiser = isAppraiserUser(user)

    const {
        first_name = '',
        last_name = '',
        email = '',
        salutation = '',
        profile_image_url: userImageUrl,
        job_title,
        phone_number,
    } = user || {}

    useEffect(() => {
        if (!userImageUrl) {
            setIsRemoveImageConfirmationOpen(false)
        }
    }, [userImageUrl])

    const defaultValues = {
        first_name,
        last_name,
        phone_number,
        job_title,
        salutation,
        team_uuid: isAppraiser ? undefined : user?.team_uuid,
        certification: isAppraiser ? user?.certification : undefined,
        locale: currentLocale,
    }
    const {
        control,
        handleSubmit,
        reset,
        formState: { dirtyFields, errors, isDirty },
        setValue,
    } = useForm<UserSettingsFormType>({
        defaultValues,
    })

    const resetFormState = useCallback(() => {
        reset(defaultValues)
        // TODO: Figure out how to get useMemo to work here. Currently it causes an infinite rerender loop
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(defaultValues), reset])

    useEffect(() => {
        resetFormState()
    }, [resetFormState])

    const handleUserSettingsSubmit = useCallback(
        ({ first_name, last_name, salutation, phone_number, job_title, team_uuid, certification, locale }: UserSettingsFormType) => {
            updateUserSettings(
                {
                    first_name,
                    last_name,
                    salutation: salutation === '-' ? undefined : salutation,
                    phone_number,
                    job_title,
                    team_uuid,
                    profile_image_url: userImageUrl,
                    email,
                    certification: isAppraiser ? certification : undefined,
                    locale,
                },
                { isLocaleDirty: !!dirtyFields.locale }
            )
        },
        [dirtyFields.locale, email, isAppraiser, updateUserSettings, userImageUrl]
    )

    const onSubmit = useMemo(() => handleSubmit(handleUserSettingsSubmit), [handleSubmit, handleUserSettingsSubmit])

    const onReset = useCallback(() => {
        reset({
            first_name,
            last_name,
            phone_number,
            job_title,
            salutation,
            team_uuid: !isAppraiser ? user?.team_uuid : undefined,
            certification: isAppraiser ? user?.certification : undefined,
            locale: currentLocale,
        })
    }, [
        currentLocale,
        first_name,
        isAppraiser,
        job_title,
        last_name,
        phone_number,
        reset,
        salutation,
        user?.certification,
        user?.team_uuid,
    ])

    useEffect(() => {
        if (layout === 'modal') {
            onModalFormStateChange?.({ isDirty, isPersisting: isUpdatingUserSettings, onCancel: onReset, onSave: onSubmit })
        }
    }, [isDirty, isUpdatingUserSettings, layout, onModalFormStateChange, onReset, onSubmit])

    const handleUploadImageClick = () => {
        profileImageInputRef.current?.click()
    }

    if (layout === 'modal') {
        const modalCopy = {
            profileImageLabel: copy.modal?.profileImageLabel ?? USER_SETTINGS_COPY.modal.profileImageLabel,
            uploadImageLabel: copy.modal?.uploadImageLabel ?? USER_SETTINGS_COPY.modal.uploadImageLabel,
            removeImageLabel: copy.modal?.removeImageLabel ?? USER_SETTINGS_COPY.modal.removeImageLabel,
            removeImageConfirmation: copy.modal?.removeImageConfirmation ?? USER_SETTINGS_COPY.modal.removeImageConfirmation,
            rowLabels: copy.modal?.rowLabels ?? USER_SETTINGS_COPY.modal.rowLabels,
        }

        if (isUserPending) {
            return (
                <ModalContentContainer>
                    <LoadingSkeleton count={6} height="46px" sx={{ transform: 'none' }} />
                </ModalContentContainer>
            )
        }

        return (
            <ModalForm aria-label={copy.labels.header} onSubmit={onSubmit}>
                <ProfileSection direction="row">
                    <ProfileImagePanel $hasImage={!!userImageUrl}>
                        {!userImageUrl && <ProfileImageWatermark />}
                        {isUploadingImage ? (
                            <CircularProgress size={tokens.SizingIconM} sx={{ color: tokens.ColorIconBoldest }} />
                        ) : (
                            <>
                                <PiazzaTypography variant="headline4" color={tokens.ColorTextBoldest}>
                                    {modalCopy.profileImageLabel}
                                </PiazzaTypography>
                                {userImageUrl && (
                                    <ProfileImagePreview>
                                        <CircularImage
                                            src={user?.profile_thumbnail_image_url}
                                            fallbackImageSrc={[userImageUrl]}
                                            alt={copy.modal?.profileImageLabel ?? modalCopy.profileImageLabel}
                                            width={96}
                                            height={96}
                                        />
                                    </ProfileImagePreview>
                                )}
                                <ProfileImageActions direction="row">
                                    <TextButtonNew
                                        label={modalCopy.uploadImageLabel}
                                        leftIcon={<UploadSimpleIcon />}
                                        variant="secondary"
                                        onClick={handleUploadImageClick}
                                        disabled={!uploadImage || isUploadingImage}
                                    />
                                    <TextButtonNew
                                        label={modalCopy.removeImageLabel}
                                        leftIcon={<XIcon />}
                                        variant="secondary"
                                        onClick={() => setIsRemoveImageConfirmationOpen(true)}
                                        disabled={!removeImage || !userImageUrl}
                                    />
                                </ProfileImageActions>
                            </>
                        )}
                        <input
                            hidden
                            onChange={(event) => {
                                const selectedFile = event.target.files?.[0]
                                if (selectedFile && uploadImage) {
                                    uploadImage(selectedFile)
                                }
                                event.target.value = ''
                            }}
                            accept=".png,.jpg,.jpeg"
                            type="file"
                            ref={profileImageInputRef}
                            data-testid="user-image-upload"
                        />
                    </ProfileImagePanel>
                    <ProfileFields>
                        <FormDropdown label={copy.labels.salutation} name="salutation" options={copy.salutationOptions} control={control} />
                        <FormTextInput label={copy.labels.first_name} name="first_name" control={control} />
                        <FormTextInput label={copy.labels.last_name} name="last_name" control={control} />
                    </ProfileFields>
                </ProfileSection>
                <SettingsFields>
                    <SettingsFieldRow label={modalCopy.rowLabels.job_title} icon={<IdentificationCardIcon weight="bold" />}>
                        <FormTextInput name="job_title" control={control} />
                    </SettingsFieldRow>
                    {isAppraiser && (
                        <SettingsFieldRow label={modalCopy.rowLabels.certification} icon={<CertificateIcon weight="bold" />}>
                            <FormTextInput name="certification" control={control} />
                        </SettingsFieldRow>
                    )}
                    {teams.length > 0 && (
                        <SettingsFieldRow label={modalCopy.rowLabels.team} icon={<UsersThreeIcon weight="bold" />}>
                            <FormDropdown
                                name="team_uuid"
                                options={teams.map(({ name, uuid }) => ({ label: name, value: uuid }))}
                                control={control}
                                rules={{ required: { value: true, message: copy.teamRequiredMessage } }}
                            />
                        </SettingsFieldRow>
                    )}
                    <SettingsFieldRow label={modalCopy.rowLabels.email} icon={<EnvelopeIcon weight="bold" />}>
                        <TextInput value={email} disabled />
                    </SettingsFieldRow>
                    <SettingsFieldRow label={modalCopy.rowLabels.phone_number} icon={<PhoneIcon weight="bold" />}>
                        <FormTextInput
                            name="phone_number"
                            control={control}
                            type="tel"
                            rules={{ validate: (value) => validators.isMobilePhone(value) || copy.invalidPhoneNumberMessage }}
                        />
                    </SettingsFieldRow>
                    {showLocaleSetting && localeOptions && localeOptions.length > 0 && (
                        <SettingsFieldRow label={modalCopy.rowLabels.locale} icon={<TranslateIcon weight="bold" />}>
                            <FormDropdown name="locale" options={localeOptions} control={control} />
                        </SettingsFieldRow>
                    )}
                </SettingsFields>
                {callToAction && <ModalCallToAction>{callToAction}</ModalCallToAction>}
                <ConfirmationDialog
                    open={isRemoveImageConfirmationOpen}
                    title={modalCopy.removeImageConfirmation.title}
                    dialogContent={modalCopy.removeImageConfirmation.message}
                    confirmLabel={modalCopy.removeImageLabel}
                    cancelLabel={modalCopy.removeImageConfirmation.cancelLabel}
                    destructive
                    isPending={isRemovingImage}
                    onCancel={() => setIsRemoveImageConfirmationOpen(false)}
                    onConfirm={() => removeImage?.()}
                />
            </ModalForm>
        )
    }

    return (
        <Stack direction="row" paddingX={PADDING_X} paddingTop={theme.spacing(12)} width={'100%'}>
            <Box minWidth="580px" width="60%" maxWidth="640px">
                {isUserPending ? (
                    <Grid xs={12} item>
                        <LoadingSkeleton count={4} height="75px" />
                    </Grid>
                ) : (
                    <FormTemplate
                        onCancel={onReset}
                        onSubmit={onSubmit}
                        hideFooter={!isDirty}
                        isFormDirty={isDirty}
                        isPersisting={isUpdatingUserSettings}
                        confirmationDialogTitle={copy.discardChangesDialogTitle}
                        confirmationDialogConfirmLabel={copy.discardChangesConfirmLabel}
                        confirmationDialogCancelLabel={copy.discardChangesCancelLabel}
                    >
                        <FormSelectField
                            label={copy.labels.salutation}
                            fieldName="salutation"
                            xs={3.5}
                            defaultValue={salutation}
                            options={copy.salutationOptions}
                            control={control}
                            errors={errors}
                        />
                        <FormTextField label={copy.labels.first_name} fieldName="first_name" xs={4.25} control={control} errors={errors} />
                        <FormTextField label={copy.labels.last_name} fieldName="last_name" xs={4.25} control={control} errors={errors} />
                        <FormTextField label={copy.labels.job_title} fieldName="job_title" control={control} errors={errors} fullWidth />
                        <FormTextField
                            label={copy.labels.phone_number}
                            fieldName="phone_number"
                            control={control}
                            errors={errors}
                            fullWidth
                            rules={{ validate: (value) => validators.isMobilePhone(value) || copy.invalidPhoneNumberMessage }}
                        />
                        {showLocaleSetting && localeOptions && localeOptions.length > 0 && (
                            <FormSelectField
                                label={copy.labels.locale}
                                fieldName="locale"
                                options={localeOptions}
                                control={control}
                                errors={errors}
                                fullWidth
                            />
                        )}
                        {isAppraiser && (
                            <FormTextField
                                label={copy.labels.certification}
                                fieldName="certification"
                                control={control}
                                errors={errors}
                                fullWidth
                            />
                        )}
                        {teams.length > 0 && (
                            <>
                                <Grid xs={12} item>
                                    <Typography variant="headline2">{copy.teamSectionTitle}</Typography>
                                </Grid>
                                <FormSelectField
                                    required
                                    label={copy.labels.team}
                                    fieldName="team_uuid"
                                    options={teams.map(({ name, uuid }) => ({ label: name, value: uuid }))}
                                    control={control}
                                    errors={errors}
                                    clear={() => setValue('team_uuid', undefined, { shouldDirty: true })}
                                    fullWidth
                                    requiredMessage={copy.teamRequiredMessage}
                                />
                            </>
                        )}
                    </FormTemplate>
                )}
                <Stack direction="column" gap={theme.spacing(2)} paddingTop={theme.spacing(6)}>
                    <FormInputLabel typographyVariant="headline2" id={'email-field-label'} label={copy.labels.email} />
                    <EmailText xs={12} label={email} aria-labelledby={'email-field-label'} />
                </Stack>
                {callToAction}
            </Box>
        </Stack>
    )
}

const EmailText = styled(FormInputLabel)(({ theme }) => ({
    color: theme.palette.customtext.black100,
}))

function SettingsFieldRow({
    children,
    label,
    icon,
}: {
    children: ReactElement<{ 'aria-labelledby'?: string }>
    label: string
    icon: ReactElement
}) {
    const labelId = useId()

    return (
        <>
            <SettingsLabel direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                <SettingsLabelIcon>{icon}</SettingsLabelIcon>
                <PiazzaTypography id={labelId} variant="headline4" color={tokens.ColorTextBoldest} noWrap>
                    {label}
                </PiazzaTypography>
            </SettingsLabel>
            <SettingsInput>{cloneElement(children, { 'aria-labelledby': labelId })}</SettingsInput>
        </>
    )
}

const ModalContentContainer = styled(Stack)`
    width: 100%;
    padding: ${tokens.SizingSpacing300};
    box-sizing: border-box;
`

const ModalForm = styled('form')`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: ${tokens.SizingSpacing300};
    gap: ${tokens.SizingSpacing400};
    box-sizing: border-box;
`

const ModalCallToAction = styled(Stack)`
    align-items: flex-end;
    margin-top: ${tokens.SizingSpacing300};
`

const ProfileSection = styled(Stack)`
    align-items: center;
    gap: ${tokens.SizingSpacing400};
`

const ProfileFields = styled(Stack)`
    flex: 1;
    min-width: 0;
    gap: ${tokens.SizingSpacing150};
`

const SettingsFields = styled('div')`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 4fr);
    width: 100%;
    column-gap: ${tokens.SizingSpacing400};
    row-gap: ${tokens.SizingSpacing150};
`

const SettingsInput = styled('div')`
    min-width: 0;
`

const ProfileImagePanel = styled(Stack, { shouldForwardProp: (prop: string) => prop[0] !== '$' })<{ $hasImage: boolean }>(
    ({ $hasImage }) => ({
        position: 'relative',
        height: '194px',
        flex: 1,
        minWidth: 0,
        justifyContent: 'center',
        alignItems: 'center',
        gap: tokens.SizingSpacing150,
        overflow: 'hidden',
        borderRadius: tokens.SizingCornerM,
        backgroundColor: $hasImage ? tokens.ColorSurfaceAccentRoyalDefault : tokens.ColorSurfaceInverseDefault,
        padding: tokens.SizingSpacing150,
        boxSizing: 'border-box',
    })
)

const ProfileImageWatermark = styled('div')`
    position: absolute;
    inset: 0;
    background-image: url('/skyscraper-header.png');
    background-size: cover;
    background-position: center;
    filter: grayscale(1);
    opacity: 0.1;
    pointer-events: none;
`

const ProfileImagePreview = styled(Stack)`
    width: 96px;
    height: 96px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: ${tokens.ColorSurfaceDefault};
    box-shadow: 0 8px 24px rgb(10 24 102 / 14%);
`

const ProfileImageActions = styled(Stack)`
    align-items: center;
    justify-content: center;
    gap: ${tokens.SizingSpacing100};
    flex-wrap: nowrap;
`

const SettingsLabel = styled(Stack)`
    height: 46px;
    min-width: 0;
`

const SettingsLabelIcon = styled(Stack)`
    flex-shrink: 0;
    color: ${tokens.ColorIconBoldest};

    svg {
        width: ${tokens.SizingIconS};
        height: ${tokens.SizingIconS};
    }
`

export default UserSettings
